import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import CCText from '@/Atoms/CCText';
import type {ClubMemberRow} from '@/Hooks/ClubsApi/types';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

type Props = {
  visible: boolean;
  clubName: string;
  members: ClubMemberRow[];
  requiresReplacement: boolean;
  isLastMember: boolean;
  submitting?: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: (replacementAdminId?: number) => void;
};

const displayName = (member: ClubMemberRow) =>
  member.name?.trim() || member.username;

export default function LeaveClubModal({
  visible,
  clubName,
  members,
  requiresReplacement,
  isLastMember,
  submitting,
  error,
  onClose,
  onConfirm,
}: Props) {
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (visible) {
      setSelectedId(null);
    }
  }, [visible]);

  if (!visible) {
    return null;
  }

  const canConfirm =
    !submitting && (!requiresReplacement || selectedId != null);

  return (
    <Modal
      transparent
      visible={visible}
      animationType={isWide ? 'fade' : 'slide'}
      onRequestClose={submitting ? undefined : onClose}>
      <Pressable
        testID="leave-club-backdrop"
        style={[styles.backdrop, isWide ? styles.center : styles.bottom]}
        onPress={submitting ? undefined : onClose}>
        <Pressable
          testID="leave-club-modal"
          style={[styles.sheet, isWide ? styles.card : styles.bottomSheet]}
          onPress={() => {}}>
          {!isWide ? <View style={styles.grabber} /> : null}

          <View style={styles.iconCircle}>
            <CCText style={styles.icon}>↗</CCText>
          </View>
          <CCText
            fontWeight="thunder"
            fontSize={isWide ? 28 : 32}
            style={styles.title}>
            Leave {clubName}?
          </CCText>

          {requiresReplacement ? (
            <>
              <CCText style={styles.body}>
                You are the club's only admin. Choose a member to take over
                before you leave.
              </CCText>
              <View style={styles.notice}>
                <CCText style={styles.noticeIcon}>♛</CCText>
                <View style={styles.noticeCopy}>
                  <CCText style={styles.noticeTitle}>
                    Keep the club running
                  </CCText>
                  <CCText style={styles.noticeText}>
                    The selected member becomes an admin immediately.
                  </CCText>
                </View>
              </View>
              <CCText style={styles.label}>Choose the next admin</CCText>
              <ScrollView
                style={styles.memberList}
                contentContainerStyle={styles.memberListContent}
                showsVerticalScrollIndicator={members.length > 4}>
                {members.map(member => {
                  const selected = selectedId === member.user_id;
                  return (
                    <Pressable
                      key={member.user_id}
                      testID={`leave-replacement-${member.user_id}`}
                      onPress={() => setSelectedId(member.user_id)}
                      style={[
                        styles.memberRow,
                        selected && styles.memberRowSelected,
                      ]}>
                      <View
                        style={[
                          styles.avatar,
                          selected && styles.avatarSelected,
                        ]}>
                        <CCText
                          style={[
                            styles.avatarText,
                            selected && styles.avatarTextSelected,
                          ]}>
                          {displayName(member).charAt(0).toUpperCase()}
                        </CCText>
                      </View>
                      <View style={styles.memberCopy}>
                        <CCText style={styles.memberName} numberOfLines={1}>
                          {displayName(member)}
                        </CCText>
                        <CCText style={styles.memberUsername} numberOfLines={1}>
                          @{member.username}
                        </CCText>
                      </View>
                      <View
                        style={[
                          styles.radio,
                          selected && styles.radioSelected,
                        ]}>
                        {selected ? <View style={styles.radioDot} /> : null}
                      </View>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </>
          ) : (
            <CCText style={styles.body}>
              {isLastMember
                ? 'You are the last member. Leaving will archive this club.'
                : 'You will lose access to the club room, chat, events, and member leaderboard. You can join again later if the club allows it.'}
            </CCText>
          )}

          {error ? (
            <View style={styles.errorBox}>
              <CCText style={styles.errorText}>{error}</CCText>
            </View>
          ) : null}

          <View style={styles.actions}>
            <Pressable
              testID="leave-club-cancel"
              disabled={submitting}
              onPress={onClose}
              style={styles.cancelButton}>
              <CCText style={styles.cancelText}>Stay in club</CCText>
            </Pressable>
            <Pressable
              testID="leave-club-confirm"
              disabled={!canConfirm}
              onPress={() => onConfirm(selectedId ?? undefined)}
              style={[
                styles.leaveButton,
                !canConfirm && styles.disabledButton,
              ]}>
              {submitting ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <CCText style={styles.leaveText}>
                  {requiresReplacement ? 'Promote & leave' : 'Leave club'}
                </CCText>
              )}
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(38, 35, 34, 0.46)',
  },
  center: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  bottom: {
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: '#FFFCF6',
    borderColor: '#E8DFCC',
    borderWidth: 1,
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 500,
    borderRadius: 24,
  },
  bottomSheet: {
    maxHeight: '88%',
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingBottom: 32,
  },
  grabber: {
    width: 42,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#CFC6B5',
    alignSelf: 'center',
    marginBottom: 20,
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FCE8E4',
    marginBottom: 14,
  },
  icon: {
    color: '#B84A3A',
    fontSize: 25,
    fontWeight: '700',
    transform: [{rotate: '45deg'}],
  },
  title: {
    color: '#262322',
    marginBottom: 8,
  },
  body: {
    color: '#6D635E',
    fontSize: 14.5,
    lineHeight: 21,
    marginBottom: 18,
  },
  notice: {
    flexDirection: 'row',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    backgroundColor: '#FFF4D2',
    borderWidth: 1,
    borderColor: '#F4D675',
    marginBottom: 18,
  },
  noticeIcon: {
    color: '#9A6B00',
    fontSize: 21,
  },
  noticeCopy: {
    flex: 1,
  },
  noticeTitle: {
    color: '#5A4210',
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 2,
  },
  noticeText: {
    color: '#786226',
    fontSize: 12.5,
    lineHeight: 18,
  },
  label: {
    color: '#4D3F37',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 9,
  },
  memberList: {
    maxHeight: 250,
    marginBottom: 18,
  },
  memberListContent: {
    gap: 8,
  },
  memberRow: {
    minHeight: 62,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderWidth: 1,
    borderColor: '#E4DED4',
    borderRadius: 14,
    backgroundColor: '#fff',
  },
  memberRowSelected: {
    borderColor: '#3DAB9E',
    backgroundColor: '#EDF9F6',
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EEE7D9',
    marginRight: 11,
  },
  avatarSelected: {
    backgroundColor: '#3DAB9E',
  },
  avatarText: {
    color: '#6D635E',
    fontWeight: '700',
  },
  avatarTextSelected: {
    color: '#fff',
  },
  memberCopy: {
    flex: 1,
    minWidth: 0,
  },
  memberName: {
    color: '#262322',
    fontSize: 14,
    fontWeight: '700',
  },
  memberUsername: {
    color: '#8A817C',
    fontSize: 12,
    marginTop: 2,
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: '#C8C0B5',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 10,
  },
  radioSelected: {
    borderColor: '#3DAB9E',
  },
  radioDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#3DAB9E',
  },
  errorBox: {
    backgroundColor: '#FCE8E4',
    borderRadius: 10,
    padding: 10,
    marginBottom: 14,
  },
  errorText: {
    color: '#A33A2D',
    fontSize: 12.5,
    lineHeight: 18,
  },
  actions: {
    flexDirection: 'row',
    gap: 10,
  },
  cancelButton: {
    flex: 1,
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 13,
    borderWidth: 1,
    borderColor: '#D8D0C5',
    backgroundColor: '#fff',
    paddingHorizontal: 12,
  },
  cancelText: {
    color: '#4D3F37',
    fontWeight: '700',
    fontSize: 14,
  },
  leaveButton: {
    flex: 1,
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 13,
    backgroundColor: '#B84A3A',
    paddingHorizontal: 12,
  },
  disabledButton: {
    opacity: 0.45,
  },
  leaveText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
  },
});
